/**
 * EAA-1670: the "currently selected tenant" scope — previously
 * `contentSlice.filters.tenant_id`, even though it's read by 10+ files across
 * unrelated domains (Programs, Tags, TagTypes, ContentTypes, Documents,
 * ThemeEditor, WebContentCoreProvider, useCustomTheme, both AiGeneratedPreview
 * modals) that have nothing to do with content CRUD. Pulled out into its own
 * Zustand store so the content domain's TanStack Query migration doesn't drag
 * 10 unrelated files into depending on it. See
 * docs/plans/fe-restructure/phase-7-state-migration.md.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { getLastSelectedTenantId, setLastSelectedTenantId } from '@/utils/localUserPreferences';

/**
 * EAA-1694: the tenant this page is about, taken from `?tenant_id=` in the URL.
 *
 * The store's only seed used to be the last pick in `localStorage`, and `MainLayout`'s
 * restore effect pins a tenant ONLY for an identity with a single membership (EAA-1817).
 * So a member of several tenants arriving with nothing stored — a new browser, cleared
 * site data, or simply a first visit — had `selectedTenantId === null`, which means the
 * API layer sends no `X-Tenant-Id`; and with more than one membership and no header the
 * backend deliberately resolves NO acting tenant, leaving the caller authenticated with
 * zero permissions. Every tenant-scoped call then failed with 403
 * ERROR_PERMISSION_DENIED — reported as an image upload and `content/ai-types/` refusing
 * an account whose role plainly granted both.
 *
 * Deep links into tenant-scoped screens already carry the answer (`?tenant_id=`, which
 * `ContentDetailsEditor` and the theme initialiser were reading for their own purposes),
 * so it seeds the store here instead of being read once and discarded.
 *
 * Read at module scope, NOT in an effect: the first queries fire during initial render,
 * which is exactly when the failing calls in the report were made.
 *
 * Trusting the URL grants nothing. The backend independently rejects an `X-Tenant-Id`
 * naming a tenant the identity has no membership in, so this can only ever pick among
 * tenants the caller already belongs to.
 */
function getTenantIdFromUrl(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('tenant_id');
        return fromUrl && fromUrl.trim() !== '' ? fromUrl : null;
    } catch {
        return null;
    }
}

/**
 * The URL wins over the stored pick — it describes the screen being opened, while the
 * stored value only records where the user was last. Adopting it also persists it, so
 * navigating on to a screen without the param keeps the same scope instead of silently
 * reverting mid-session.
 */
function initialSelectedTenantId(): string | null {
    const fromUrl = getTenantIdFromUrl();
    if (fromUrl) {
        setLastSelectedTenantId(fromUrl);
        return fromUrl;
    }
    return getLastSelectedTenantId();
}

interface SelectedTenantStore {
    selectedTenantId: string | null;
    setSelectedTenantId: (tenantId: string | null | undefined) => void;
}

export const useSelectedTenantStore = create<SelectedTenantStore>()(
    devtools(
        (set) => ({
            selectedTenantId: initialSelectedTenantId(),
            setSelectedTenantId: (tenantId) => {
                const normalized = tenantId ?? null;
                setLastSelectedTenantId(normalized);
                set({ selectedTenantId: normalized }, false, 'selectedTenant/set');
            },
        }),
        { name: 'selectedTenant' }
    )
);
